import React from "react";
import PropTypes from "prop-types";

// Human-readable labels for common vendor fields
const FIELD_LABELS = {
  name:                 "Name",
  name_2:               "Name Line 2",
  account_group:        "Account Group",
  vendor_type:          "Vendor Type",
  vendor_category:      "Vendor Category",
  pan_number:           "PAN Number",
  gstin:                "GSTIN",
  email:                "Email",
  phone:                "Phone",
  website:              "Website",
  payment_terms:        "Payment Terms",
  currency:             "Currency",
  company_code:         "Company Code",
  plant_code:           "Plant Code",
  purchase_organisation:"Purchase Organisation",
  inco_terms:           "Inco Terms",
  order_currency:       "Order Currency",
  recon_account:        "Reconciliation Account",
  bank_account_number:  "Bank Account Number",
  bank_ifsc:            "Bank IFSC",
  bank_name:            "Bank Name",
  address_line1:        "Address Line 1",
  address_line2:        "Address Line 2",
  city:                 "City",
  state:                "State",
  postal_code:          "Postal Code",
  country:              "Country",
  withholding_tax_type: "Withholding Tax Type",
  withholding_tax_code: "Withholding Tax Code",
  section_code:         "Section Code",
};

const label = (key) => FIELD_LABELS[key] || key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Returns true if the value is non-empty
const nonEmpty = (v) => v !== null && v !== undefined && String(v).trim() !== "";

// VendorChangesDiff renders a table comparing old vs new field values.
// Only rows where old !== new are shown by default; toggle to show all.
function VendorChangesDiff({ oldData, newData }) {
  const [showAll, setShowAll] = React.useState(false);

  if (!oldData || !newData) {
    return <div style={{ color: "#94A3B8", fontSize: 13, padding: 20 }}>No change data available.</div>;
  }

  // Collect all keys from both objects, excluding internal keys
  const allKeys = [...new Set([...Object.keys(oldData), ...Object.keys(newData)])]
    .filter((k) => !k.startsWith("_") && k !== "vendor_id" && k !== "created_at" && k !== "updated_at");

  const changedKeys = allKeys.filter((k) => {
    const o = String(oldData[k] ?? "").trim();
    const n = String(newData[k] ?? "").trim();
    return o !== n && (nonEmpty(oldData[k]) || nonEmpty(newData[k]));
  });

  const displayKeys = showAll ? allKeys.filter((k) => nonEmpty(oldData[k]) || nonEmpty(newData[k])) : changedKeys;

  if (displayKeys.length === 0) {
    return <div style={{ color: "#94A3B8", fontSize: 13, padding: 20 }}>No field changes detected.</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <span style={{ fontSize: 12, color: "#64748B" }}>
          {changedKeys.length} field{changedKeys.length !== 1 ? "s" : ""} changed
        </span>
        <button
          className="t2t-btn t2t-btn-ghost t2t-btn-sm"
          onClick={() => setShowAll((s) => !s)}
          style={{ fontSize: 11 }}
        >
          {showAll ? "Show Changes Only" : "Show All Fields"}
        </button>
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ background: "#F8FAFC" }}>
            <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#64748B", border: "1px solid #E2E8F0", width: "28%" }}>Field</th>
            <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#64748B", border: "1px solid #E2E8F0" }}>Current Value</th>
            <th style={{ padding: "6px 10px", textAlign: "left", fontWeight: 700, color: "#64748B", border: "1px solid #E2E8F0" }}>Proposed Value</th>
          </tr>
        </thead>
        <tbody>
          {displayKeys.map((k) => {
            const oldVal = oldData[k];
            const newVal = newData[k];
            const changed = String(oldVal ?? "").trim() !== String(newVal ?? "").trim();
            return (
              <tr key={k} style={{ background: changed ? "#FFFBEB" : "white" }}>
                <td style={{ padding: "6px 10px", border: "1px solid #E2E8F0", fontWeight: 600, color: "#374151" }}>
                  {label(k)}
                </td>
                <td style={{ padding: "6px 10px", border: "1px solid #E2E8F0", color: changed ? "#DC2626" : "#374151", textDecoration: changed ? "line-through" : "none" }}>
                  {nonEmpty(oldVal) ? String(oldVal) : <span style={{ color: "#D1D5DB" }}>—</span>}
                </td>
                <td style={{ padding: "6px 10px", border: "1px solid #E2E8F0", color: changed ? "#16A34A" : "#374151", fontWeight: changed ? 600 : 400 }}>
                  {nonEmpty(newVal) ? String(newVal) : <span style={{ color: "#D1D5DB" }}>—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

VendorChangesDiff.propTypes = {
  oldData: PropTypes.object,
  newData: PropTypes.object,
};

export default VendorChangesDiff;
